import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorBox, ProvenanceBadge, Skeleton } from "../components/ui";
import {
  draftWorkforceAi, draftWorkforceRule, searchWorkforce, useExpectedSkills, usePendingRedeployments, useProposeRedeployment,
  useRedeploymentCandidates, useSetExpectedSkills, useWorkforceCatalogue,
  type WorkforceDraft, type WorkforceResult,
} from "../lib/api";

type Props = { company?: string };

const emptySpec = { skills: [] as string[], min_proficiency: 0, category: null as string | null, designation: null as string | null, company: null as string | null };

/** "Propose redeployment to…" — a target designation, an optional company (defaults to the employee's own), a
 * note, then a draft Employee Transfer (docstatus 0) for HR to review — shared by the search results and the
 * redeployment candidate list. */
function ProposeButton({ employee, defaultCompany }: { employee: string; defaultCompany: string }) {
  const { t } = useTranslation();
  const catalogue = useWorkforceCatalogue(true);
  const propose = useProposeRedeployment();
  const [open, setOpen] = useState(false);
  const [designation, setDesignation] = useState("");
  const [company, setCompany] = useState(defaultCompany);
  const [note, setNote] = useState("");
  const [done, setDone] = useState<string | null>(null);

  if (done) return <span className="text-xs font-semibold text-status-green">{t("wf.proposed")} · {done}</span>;
  if (!open) return <button onClick={() => setOpen(true)} className="rounded border border-brand-primary px-2 py-0.5 text-xs font-semibold text-brand-primary hover:bg-blue-50" data-workforce-propose-open={employee}>{t("wf.propose")}</button>;

  return (
    <div className="flex flex-wrap items-center gap-1" data-workforce-propose-panel={employee}>
      <select value={designation} onChange={(e) => setDesignation(e.target.value)} className="rounded border border-brand-grey-light px-1 py-1 text-xs" data-workforce-propose-designation>
        <option value="" disabled>{t("wf.chooseDesignation")}</option>
        {(catalogue.data?.designations ?? []).map((d) => <option key={d} value={d}>{d}</option>)}
      </select>
      <select value={company} onChange={(e) => setCompany(e.target.value)} className="rounded border border-brand-grey-light px-1 py-1 text-xs">
        {(catalogue.data?.companies ?? []).map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("wf.notePlaceholder")} className="w-36 rounded border border-brand-grey-light px-2 py-1 text-xs" />
      <button disabled={!designation || propose.isPending} onClick={() => propose.mutate({ employee, designation, company: company || undefined, note: note || undefined }, { onSuccess: (r) => setDone(r.employee_transfer) })}
        className="rounded bg-brand-primary px-2 py-0.5 text-xs font-semibold text-white disabled:opacity-50" data-workforce-propose-confirm={employee}>
        {t("wf.confirm")}
      </button>
      <button onClick={() => setOpen(false)} className="text-xs text-brand-grey hover:text-brand-charcoal">{t("common.cancel")}</button>
      {propose.isError && <ErrorBox error={propose.error} />}
    </div>
  );
}

function ResultRow({ r }: { r: WorkforceResult }) {
  const { t } = useTranslation();
  return (
    <tr className="border-t border-brand-grey-light" data-workforce-result={r.name}>
      <td className="px-3 py-2"><div className="font-semibold">{r.employee_name}</div><div className="text-[11px] text-brand-grey">{r.designation} · {r.company}</div></td>
      <td className="px-3 py-2 text-xs">
        {r.matched_skills.length === 0 ? <span className="text-brand-grey">{t("wf.search.noMatch")}</span> : (
          <div className="flex flex-wrap gap-1">
            {r.matched_skills.map((s) => <span key={s.skill} className="rounded border border-brand-grey-light px-1.5 py-0.5">{s.skill} <span className="text-amber-500">{"★".repeat(Math.round((s.proficiency || 0) * 5))}</span></span>)}
          </div>
        )}
      </td>
      <td className="px-3 py-2 text-right text-xs font-semibold tabular-nums">{Math.round(r.avg_proficiency * 100)} %</td>
      <td className="px-3 py-2 text-right"><ProposeButton employee={r.name} defaultCompany={r.company} /></td>
    </tr>
  );
}

/** Workforce search (structured + semantic) and redeployment recommendation (WP-11.2, HR-05, HR-06): a
 * natural-language or filter-based search over rated skills, and a skill-gap ranking of active employees against
 * one designation's curated expected skills — every "propose" only drafts an Employee Transfer for HR to decide. */
export default function Workforce({ company }: Props) {
  const { t } = useTranslation();
  const catalogue = useWorkforceCatalogue(true);
  const [instruction, setInstruction] = useState("");
  const [spec, setSpec] = useState(emptySpec);
  const [draftMeta, setDraftMeta] = useState<WorkforceDraft | null>(null);
  const [results, setResults] = useState<WorkforceResult[] | null>(null);
  const [busy, setBusy] = useState<"rule" | "ai" | "search" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const skillsByCategory = useMemo(() => {
    const out = new Map<string, string[]>();
    for (const s of catalogue.data?.skills ?? []) out.set(s.category ?? "—", [...(out.get(s.category ?? "—") ?? []), s.skill_name]);
    return [...out.entries()];
  }, [catalogue.data]);

  const doDraft = async (mode: "rule" | "ai") => {
    if (!instruction.trim()) return;
    setBusy(mode); setError(null);
    try {
      const d = mode === "ai" ? await draftWorkforceAi(instruction, "en") : await draftWorkforceRule(instruction);
      setDraftMeta(d);
      if (d.spec) setSpec({ ...emptySpec, ...d.spec, company: d.spec.company ?? company ?? null });
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setBusy(null); }
  };
  const doSearch = async () => {
    setBusy("search"); setError(null);
    try { setResults(await searchWorkforce({ ...spec, company: spec.company ?? company })); } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setBusy(null); }
  };
  const toggleSkill = (s: string) => setSpec((cur) => ({ ...cur, skills: cur.skills.includes(s) ? cur.skills.filter((x) => x !== s) : [...cur.skills, s] }));

  return (
    <div className="mt-4 space-y-4" data-workforce-page>
      <section className="rounded border border-brand-grey-light bg-white p-4">
        <h2 className="font-heading text-base font-semibold">{t("wf.search.title")}</h2>
        <p className="text-xs text-brand-grey">{t("wf.search.hint")}</p>
        <textarea value={instruction} onChange={(e) => setInstruction(e.target.value)} rows={2} placeholder={t("wf.search.placeholder")}
          className="mt-2 w-full rounded border border-brand-grey-light px-3 py-2 text-sm focus:border-brand-primary focus:outline-none" data-workforce-instruction />
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <button disabled={!!busy || !instruction.trim()} onClick={() => void doDraft("rule")} data-workforce-draft="rule" className="rounded bg-brand-primary px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50">{busy === "rule" ? t("wf.search.drafting") : t("wf.search.draftRule")}</button>
          <button disabled={!!busy || !instruction.trim()} onClick={() => void doDraft("ai")} data-workforce-draft="ai" className="rounded bg-violet-600 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50">✦ {busy === "ai" ? t("wf.search.drafting") : t("wf.search.draftAi")}</button>
          {draftMeta && <ProvenanceBadge source={draftMeta.source} />}
          {draftMeta?.model && <span className="text-[11px] text-brand-grey">{draftMeta.model}</span>}
        </div>

        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-brand-grey">{t("wf.search.skills")}</div>
            <div className="mt-1 flex flex-wrap gap-1">
              {skillsByCategory.map(([cat, names]) => names.map((s) => (
                <button key={s} type="button" onClick={() => toggleSkill(s)} title={cat}
                  className={`rounded-full border px-2 py-0.5 text-[11px] ${spec.skills.includes(s) ? "border-brand-primary bg-blue-50 text-brand-primary" : "border-brand-grey-light text-brand-charcoal hover:border-brand-primary"}`}>
                  {s}
                </button>
              )))}
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs">
              <span className="w-24 text-brand-grey">{t("wf.search.minProficiency")}</span>
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} onClick={() => setSpec((c) => ({ ...c, min_proficiency: c.min_proficiency === n / 5 ? 0 : n / 5 }))}
                  className={`h-5 w-5 rounded border ${spec.min_proficiency >= n / 5 ? "border-brand-primary bg-blue-50 text-brand-primary" : "border-brand-grey-light"}`}>{n}</button>
              ))}
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="w-24 text-brand-grey">{t("wf.search.designation")}</span>
              <select value={spec.designation ?? ""} onChange={(e) => setSpec((c) => ({ ...c, designation: e.target.value || null }))} className="flex-1 rounded border border-brand-grey-light px-1 py-1">
                <option value="">{t("common.all")}</option>
                {(catalogue.data?.designations ?? []).map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="w-24 text-brand-grey">{t("wf.search.company")}</span>
              <select value={spec.company ?? company ?? ""} onChange={(e) => setSpec((c) => ({ ...c, company: e.target.value || null }))} className="flex-1 rounded border border-brand-grey-light px-1 py-1">
                <option value="">{t("common.all")}</option>
                {(catalogue.data?.companies ?? []).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <button disabled={busy === "search"} onClick={() => void doSearch()} data-workforce-search className="rounded border border-brand-primary px-3 py-1.5 text-sm font-semibold text-brand-primary hover:bg-blue-50 disabled:opacity-50">
            {busy === "search" ? t("wf.search.searching") : t("wf.search.run")}
          </button>
        </div>
        {error && <div className="mt-2"><ErrorBox error={error} /></div>}

        {results && (
          <div className="mt-3 overflow-x-auto" data-workforce-results>
            {results.length === 0 ? <p className="py-4 text-center text-sm text-brand-grey">{t("wf.search.noResults")}</p> : (
              <table className="w-full text-sm">
                <thead className="bg-[#F6F8FA] text-left text-[11px] uppercase tracking-wide text-brand-grey">
                  <tr><th className="px-3 py-2">{t("wf.search.employee")}</th><th className="px-3 py-2">{t("wf.search.matched")}</th><th className="px-3 py-2 text-right">{t("wf.search.avg")}</th><th className="px-3 py-2 text-right">{t("wf.action")}</th></tr>
                </thead>
                <tbody>{results.map((r) => <ResultRow key={r.name} r={r} />)}</tbody>
              </table>
            )}
          </div>
        )}
      </section>

      <RedeploymentSection company={company} />
      <PendingSection company={company} />
    </div>
  );
}

function RedeploymentSection({ company }: { company?: string }) {
  const { t } = useTranslation();
  const catalogue = useWorkforceCatalogue(true);
  const [designation, setDesignation] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editing, setEditing] = useState<string[]>([]);
  const expected = useExpectedSkills(designation, !!designation);
  const setExpected = useSetExpectedSkills();
  const [showCandidates, setShowCandidates] = useState(false);
  const candidates = useRedeploymentCandidates(designation, company, showCandidates);

  const startEditing = () => { setEditing(expected.data ?? []); setIsEditing(true); };
  const toggle = (s: string) => setEditing((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));

  return (
    <section className="rounded border border-brand-grey-light bg-white p-4" data-workforce-redeployment>
      <h2 className="font-heading text-base font-semibold">{t("wf.redeploy.title")}</h2>
      <p className="text-xs text-brand-grey">{t("wf.redeploy.hint")}</p>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
        <span className="text-brand-grey">{t("wf.redeploy.designation")}</span>
        <select value={designation} onChange={(e) => { setDesignation(e.target.value); setShowCandidates(false); setIsEditing(false); setEditing([]); }} className="rounded border border-brand-grey-light px-1 py-1" data-workforce-redeploy-designation>
          <option value="">{t("wf.chooseDesignation")}</option>
          {(catalogue.data?.designations ?? []).map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>

      {designation && (
        <div className="mt-3">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-brand-grey">{t("wf.redeploy.expectedSkills")}</div>
            {!isEditing && !expected.isLoading && <button onClick={startEditing} className="text-xs text-brand-primary hover:underline" data-workforce-edit-expected>{t("wf.redeploy.edit")}</button>}
          </div>
          {expected.isLoading ? <Skeleton className="mt-1 h-6" /> : !isEditing && (expected.data ?? []).length === 0 ? (
            <p className="mt-1 text-xs text-brand-grey">{t("wf.redeploy.noExpected")} <button onClick={startEditing} className="text-brand-primary hover:underline">{t("wf.redeploy.configure")}</button></p>
          ) : !isEditing ? (
            <div className="mt-1 flex flex-wrap gap-1">{(expected.data ?? []).map((s) => <span key={s} className="rounded-full border border-brand-grey-light px-2 py-0.5 text-[11px]">{s}</span>)}</div>
          ) : (
            <div>
              <div className="mt-1 flex flex-wrap gap-1">
                {(catalogue.data?.skills ?? []).map((s) => (
                  <button key={s.skill_name} type="button" onClick={() => toggle(s.skill_name)}
                    className={`rounded-full border px-2 py-0.5 text-[11px] ${editing.includes(s.skill_name) ? "border-brand-primary bg-blue-50 text-brand-primary" : "border-brand-grey-light"}`}>
                    {s.skill_name}
                  </button>
                ))}
              </div>
              <div className="mt-2 flex gap-2">
                <button disabled={setExpected.isPending} onClick={() => setExpected.mutate({ designation, skills: editing }, { onSuccess: () => setIsEditing(false) })}
                  className="rounded bg-brand-primary px-2 py-0.5 text-xs font-semibold text-white disabled:opacity-50" data-workforce-save-expected>{t("wf.redeploy.save")}</button>
                <button onClick={() => setIsEditing(false)} className="text-xs text-brand-grey hover:text-brand-charcoal">{t("common.cancel")}</button>
              </div>
            </div>
          )}

          {(expected.data ?? []).length > 0 && !isEditing && (
            <div className="mt-3">
              <button onClick={() => setShowCandidates(true)} className="rounded border border-brand-primary px-3 py-1.5 text-sm font-semibold text-brand-primary hover:bg-blue-50" data-workforce-show-candidates>{t("wf.redeploy.showCandidates")}</button>
              {showCandidates && (
                <div className="mt-3 overflow-x-auto">
                  {candidates.isLoading ? <Skeleton className="h-16" /> : candidates.isError ? <ErrorBox error={candidates.error} /> : (candidates.data ?? []).length === 0 ? (
                    <p className="py-4 text-center text-sm text-brand-grey">{t("wf.redeploy.noCandidates")}</p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-[#F6F8FA] text-left text-[11px] uppercase tracking-wide text-brand-grey">
                        <tr><th className="px-3 py-2">{t("wf.search.employee")}</th><th className="px-3 py-2 text-right">{t("wf.redeploy.coverage")}</th><th className="px-3 py-2">{t("wf.redeploy.missing")}</th><th className="px-3 py-2 text-right">{t("wf.action")}</th></tr>
                      </thead>
                      <tbody>
                        {(candidates.data ?? []).map((c) => (
                          <tr key={c.name} className="border-t border-brand-grey-light" data-workforce-candidate={c.name}>
                            <td className="px-3 py-2"><div className="font-semibold">{c.employee_name}</div><div className="text-[11px] text-brand-grey">{c.designation} · {c.company}</div></td>
                            <td className="px-3 py-2 text-right text-xs font-semibold tabular-nums">{c.gap.coverage_pct} %</td>
                            <td className="px-3 py-2 text-xs text-brand-grey">{c.gap.missing.join(", ") || "–"}</td>
                            <td className="px-3 py-2 text-right"><ProposeButton employee={c.name} defaultCompany={c.company} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function PendingSection({ company }: { company?: string }) {
  const { t } = useTranslation();
  const pending = usePendingRedeployments(company, true);
  return (
    <section className="rounded border border-brand-grey-light bg-white p-4" data-workforce-pending>
      <h2 className="font-heading text-base font-semibold">{t("wf.pending.title")}</h2>
      <p className="text-xs text-brand-grey">{t("wf.pending.hint")}</p>
      {pending.isLoading ? <Skeleton className="mt-2 h-10" /> : pending.isError ? <div className="mt-2"><ErrorBox error={pending.error} /></div> : (pending.data ?? []).length === 0 ? (
        <p className="mt-2 text-sm text-brand-grey">{t("wf.pending.none")}</p>
      ) : (
        <ul className="mt-2 space-y-1 text-sm">
          {(pending.data ?? []).map((p) => (
            <li key={p.name} className="flex items-center justify-between rounded border border-brand-grey-light/70 px-2 py-1.5">
              <span><span className="font-semibold">{p.employee_name}</span> <span className="text-xs text-brand-grey">{p.company}{p.new_company && p.new_company !== p.company ? ` → ${p.new_company}` : ""}</span></span>
              <span className="text-xs">{t("wf.pending.review")}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
